import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import OneSignal from 'react-onesignal'; 
import { Capacitor } from '@capacitor/core';

// =====================================
// 🌍 นำเข้าหน้าโซนสาธารณะ (ลูกค้า)
// =====================================
import BookingPage from './features/booking/BookingPage';
import TrackingPage from './features/booking/TrackingPage';
// import DownloadAppPage from './features/public/DownloadAppPage';

// =====================================
// 📱 นำเข้าหน้าของฝั่งแอปช่าง (Mobile)
// =====================================
import LoginPage from './features/auth/LoginPage';
import QueuePage from './features/queue/pages/QueuePage';
import ProfilePage from './features/profile/pages/ProfilePage';
import HistoryPage from './features/history/pages/HistoryPage';

// =====================================
// 💻 นำเข้าหน้าของฝั่งระบบ CRM (PC/Tablet)
// =====================================
import AdminLoginPage from './features/admin/AdminLoginPage';
import DashboardPage from './features/admin/DashboardPage'; 
import ApproveJobsPage from './features/admin/ApproveJobsPage';
import AdminLayout from './features/admin/AdminLayout';
import AdminProfilePage from './features/admin/AdminProfilePage';
import RolesManagementPage from './features/admin/RolesManagementPage';
import CreateJobPage from './features/admin/CreateJobPage'; 
import CustomersPage from './features/admin/CustomersPage';
import EmployeesPage from './features/admin/EmployeesPage';
import AllJobsPage from './features/admin/AllJobsPage';
import SystemLogsPage from './features/admin/SystemLogsPage';
import InspectJobsPage from './features/admin/InspectJobsPage';
import AdminChatPage from './features/admin/AdminChatPage';
import SettingsPage from './features/admin/SettingsPage';
import FinancePage from './features/admin/FinancePage';
import InvoicePage from './features/admin/InvoicePage';
import HelpdeskCreatePage from './features/admin/HelpdeskCreatePage';
import HelpdeskTicketsPage from './features/admin/HelpdeskTicketsPage';
import FacebookAutomationPage from './features/admin/FacebookAutomationPage';
import AIAgentWorkflowPage from './features/admin/AIAgentWorkflowPage';
import KPIManagementPage from './features/admin/KPIManagementPage';
import HRManagementPage from './features/admin/HRManagementPage';
import CommissionsPage from './features/admin/CommissionsPage';
import SalesApp from './features/sales/pages/SalesApp';
import SalesLoginPage from './features/sales/pages/SalesLoginPage';
import ProductsManagementPage from './features/admin/ProductsManagementPage';
import WebsiteManagerPage from './features/admin/WebsiteManagerPage';
import SalesDataPage from './features/admin/SalesDataPage';
import ExecDashboardPage from './features/admin/ExecDashboardPage';
import GoogleDrivePage from './features/admin/GoogleDrivePage';
import ExportDataPage from './features/admin/ExportDataPage';
import BlockedDatesPage from './features/admin/BlockedDatesPage';
import SEOManagerPage from './features/admin/SEOManagerPage';

// =====================================
// 📱 โครงสร้างแอปมือถือ (TechApp) 
// =====================================
const TechApp = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('queue');

  useEffect(() => {
    const runOneSignal = async () => {
      if (user) {
        try {
          if (Capacitor.isNativePlatform()) {
            // 📱 การตั้งค่าสำหรับ Mobile App (Native Push)
            if (window.plugins && window.plugins.OneSignal) {
              window.plugins.OneSignal.setAppId('ae754e9c-6ce2-4520-a074-284baef0fb3c');
              window.plugins.OneSignal.promptForPushNotificationsWithUserResponse(function(accepted) {
                console.log("User accepted notifications: " + accepted);
              });
              window.plugins.OneSignal.login(user.uid);
            } else {
              console.warn("OneSignal Cordova plugin not found on window object.");
            }
          } else {
            // 🌐 การตั้งค่าสำหรับ Web Push (PWA/Browser)
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            await OneSignal.init({ 
              appId: 'ae754e9c-6ce2-4520-a074-284baef0fb3c', 
              allowLocalhostAsSecureOrigin: isLocalhost 
            });
            await OneSignal.login(user.uid); // 🚀 ล็อกอินเชื่อมโยง UID ช่างกับ OneSignal
            await OneSignal.Slidedown.promptPush(); 
          }
        } catch (error) {
          console.error('OneSignal SDK Initialization Error:', error);
        }
      }
    };
    runOneSignal();
  }, [user]);

  if (!user) return <Navigate to="/login" />;

  return (
    <div className="bg-gray-50 min-h-screen">
      {activeTab === 'queue' && <QueuePage setActiveTab={setActiveTab} />}
      {activeTab === 'history' && <HistoryPage setActiveTab={setActiveTab} />}
      {activeTab === 'profile' && <ProfilePage setActiveTab={setActiveTab} />}
    </div>
  );
};

// =====================================
// 🛡️ ยามเฝ้าประตูฝั่ง Admin (ป้องกันคนแอบเข้า)
// =====================================
const AdminProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    // ถ้ายังไม่ล็อกอิน ให้เด้งไปหน้า Login ของ Admin
    return <Navigate to="/admin/login" replace />;
  }
  
  if (user.role === 'tech') {
    // ถ้าช่าง (tech) แอบพิมพ์ URL เข้ามา ให้เตะกลับไปหน้าแอปช่าง
    return <Navigate to="/tech" replace />;
  }

  // ถ้าผ่านเงื่อนไข (เป็น owner, admin, it) ให้เข้าดูเนื้อหาได้
  return children;
};

// =====================================
// 🛡️ ยามเฝ้าประตูฝั่ง Sales
// =====================================
const SalesProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/sales/login" replace />;
  }
  
  // อนุญาตให้เซลส์ แอดมิน และเจ้าของเข้าได้
  if (user.role !== 'sales' && user.role !== 'admin' && user.role !== 'owner' && user.role !== 'it') {
    return <Navigate to="/sales/login" replace />;
  }
  
  return children;
};

// =====================================
// 🚀 Router หลักของระบบ
// =====================================
export default function App() {

  // 🛡️ ระบบป้องกันการแอบดู Source Code (คลิกขวา & F12)
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener('contextmenu', handleContextMenu);

    const handleKeyDown = (e) => {
      if (
        e.keyCode === 123 || 
        (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74)) || 
        (e.ctrlKey && e.keyCode === 85) 
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* 🌍 โซนสาธารณะ (ลูกค้า) */}
          <Route path="/booking" element={<BookingPage />} />
          <Route path="/tracking" element={<TrackingPage />} />

          {/* 📱 โซนแอปช่าง */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/tech/*" element={<TechApp />} />
          
          {/* 💼 โซนแอปเซลส์ */}
          <Route path="/sales/login" element={<SalesLoginPage />} />
          <Route path="/sales/*" element={
            <SalesProtectedRoute>
              <SalesApp />
            </SalesProtectedRoute>
          } />
          
          <Route path="/" element={<Navigate to="/login" />} />

          {/* 💻 โซน CRM ผู้บริหาร */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          
          {/* 🚀 สังเกตตรงนี้ครับ เอา AdminProtectedRoute มาครอบส่วน AdminLayout ไว้ */}
          <Route path="/admin/*" element={
            <AdminProtectedRoute>
              <AdminLayout>
                <Routes>
                  {/* เมนูหลักแอดมิน */}
                  <Route path="dashboard" element={<DashboardPage />} />
                  
                  {/* 💬 หมวด Chat & Helpdesk (ใส่เป็นหน้าชั่วคราวกันจอขาวไว้ให้ครับ) */}
                  <Route path="chat" element={<AdminChatPage />} />
                  <Route path="facebook-automation" element={<FacebookAutomationPage />} />
                  <Route path="helpdesk/create" element={<HelpdeskCreatePage />} />
                  <Route path="helpdesk/tickets" element={<HelpdeskTicketsPage />} />
                  
                  {/* 🚧 หมวดบริหารจัดการงาน */}
                  <Route path="jobs/create" element={<CreateJobPage />} />
                  <Route path="jobs/approve" element={<ApproveJobsPage />} />
                  <Route path="jobs/inspect" element={<InspectJobsPage />} />
                  <Route path="jobs/all" element={<AllJobsPage />} />
                  
                  {/* 👥 หมวดจัดการข้อมูลหลัก */}
                  <Route path="data/customers" element={<CustomersPage />} />
                  <Route path="data/employees" element={<EmployeesPage />} />
                  <Route path="commissions" element={<CommissionsPage />} />
                  <Route path="kpi" element={<KPIManagementPage />} />
                  <Route path="hr" element={<HRManagementPage />} />
                  <Route path="products" element={<ProductsManagementPage />} />
                  <Route path="web-manager" element={<WebsiteManagerPage />} />
                  <Route path="sales-data" element={<SalesDataPage />} />
                  
                  {/* 🔐 หมวดสิทธิ์ความปลอดภัย & การเงิน */}
                  <Route path="roles" element={<RolesManagementPage />} />
                  <Route path="logs" element={<SystemLogsPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="finance" element={<FinancePage />} />
                  <Route path="finance/invoice" element={<InvoicePage />} />
                  <Route path="profile" element={<AdminProfilePage />} />
                  <Route path="ai-workflow" element={<AIAgentWorkflowPage />} />
                  <Route path="exec-dashboard" element={<ExecDashboardPage />} />
                  <Route path="google-drive" element={<GoogleDrivePage />} />
                  <Route path="export-data" element={<ExportDataPage />} />
                  <Route path="blocked-dates" element={<BlockedDatesPage />} />
                  <Route path="seo" element={<SEOManagerPage />} />
                </Routes>
              </AdminLayout>
            </AdminProtectedRoute>
          } />
        </Routes>
      </AuthProvider>
    </Router>
  );
}